import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Copy, Plus } from 'lucide-react';
import { useState } from 'react';
import { FormDefinitionFilters } from './FormDefinitionFilters';

export const Route = createFileRoute('/console/$orgId/configuration/api-integrations/form-designer/')({
    staticData: {
        ui: {
            subtitle: 'Create, edit, and manage dynamic form definitions',
        },
        metadata: {
            title: 'Forms',
        },
    },
    component: FormDesignerList,
});

type QueryParams = RouterInputs['formDesigner']['listFormDefinitions'];
type FormDefinitionRow = RouterOutputs['formDesigner']['listFormDefinitions']['rows'][number];

const columnHelper = createColumnHelper<FormDefinitionRow, NonNullable<QueryParams['orderBy']>>();

function FormDesignerList() {
    const { orgId } = Route.useParams();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();
    const { trpcUtils } = useTrpc();

    const [whereConditions, setWhereConditions] = useState<QueryParams['where']>({});
    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { updatedAt: 'desc' },
    });

    const listQuery = trpc.formDesigner.listFormDefinitions.useQuery(
        { ...queryParams, where: whereConditions },
        { placeholderData: (prev) => prev },
    );

    const { mutate: deleteDefinition } = trpc.formDesigner.deleteFormDefinition.useMutation({
        onSuccess: () => {
            void trpcUtils.formDesigner.listFormDefinitions.invalidate();
        },
        onError: (error) => errorMessage.show(error),
    });

    const { mutate: duplicateDefinition } = trpc.formDesigner.duplicateFormDefinition.useMutation({
        onSuccess: (result) => {
            message.success('Form duplicated');
            void trpcUtils.formDesigner.listFormDefinitions.invalidate();
            navigate({
                to: '/console/$orgId/configuration/api-integrations/form-designer/$formDefinitionId',
                params: { orgId, formDefinitionId: result.definition.id.toString() },
            });
        },
        onError: (error) => errorMessage.show(error),
    });

    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            cell: ({ row }) => <span className="font-medium text-accent-600">{row.name}</span>,
        }),
        columnHelper.accessor('schemaKey', {
            id: 'schemaKey',
            header: 'Schema key',
            sortable: 'schemaKey',
            cell: ({ row }) => <code className="text-xs">{row.schemaKey}</code>,
        }),
        columnHelper.display({
            id: 'version',
            header: 'Version',
            cell: ({ row }) => {
                // The server sends up to two versions, active-first — derive the
                // latest by number rather than trusting the order.
                const latest = row.Versions.reduce<FormDefinitionRow['Versions'][number] | undefined>(
                    (max, version) => (max && max.version >= version.version ? max : version),
                    undefined,
                );
                if (!latest) return '—';
                const activePublished = row.Versions.find(
                    (version) => version.isActive && version.status === 'PUBLISHED',
                );
                return (
                    <span className="whitespace-nowrap">
                        v{latest.version} <StatusBadge status={latest.status} module="formSchema" />
                        {/* A live published version behind a newer draft would
                            otherwise be invisible in this list. */}
                        {activePublished && activePublished.id !== latest.id && (
                            <StatusBadge
                                status={activePublished.status}
                                module="formSchema"
                                customLabel={`v${activePublished.version} published`}
                            />
                        )}
                    </span>
                );
            },
        }),
        columnHelper.accessor('tags', {
            id: 'tags',
            header: 'Tags',
            cell: ({ row }) =>
                row.tags?.length > 0
                    ? row.tags.map((tag) => (
                          <Tag key={tag} variant="outlined">
                              {tag}
                          </Tag>
                      ))
                    : '—',
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Updated',
            sortable: 'updatedAt',
            cell: ({ row }) => formatDate(row.updatedAt),
        }),
        columnHelper.display({
            id: 'UpdatedBy',
            header: 'Updated by',
            cell: ({ row }) => (
                <User userId={row.UpdatedBy.userId} photoUrl={row.UpdatedBy.photoUrl} name={row.UpdatedBy.fullName} />
            ),
        }),
        columnHelper.actions({
            cell: ({ row }) => {
                return (
                    <Space size="small">
                        <Button.Edit
                            size="small"
                            onClick={() => {
                                navigate({
                                    to: '/console/$orgId/configuration/api-integrations/form-designer/$formDefinitionId',
                                    params: { orgId, formDefinitionId: row.id.toString() },
                                });
                            }}
                        />
                        <Button.Icon
                            size="small"
                            color="gray"
                            tooltip="Duplicate"
                            icon={<Copy />}
                            onClick={() => {
                                // Schema keys are immutable, unique per org and capped at 100
                                // chars — the timestamp suffix must survive truncation intact
                                // or a near-limit key duplicates to itself (or collides on
                                // repeat), so the base is trimmed to make room for it.
                                const suffix = `-copy-${Date.now().toString(36)}`;
                                duplicateDefinition({
                                    formDefinitionId: row.id,
                                    newSchemaKey: `${row.schemaKey.slice(0, 100 - suffix.length)}${suffix}`,
                                });
                            }}
                        />
                        <Button.Delete
                            size="small"
                            confirmTitle={`Delete "${row.name}"?`}
                            confirmDescription={
                                row.deletionMode === 'archive'
                                    ? 'This form has a non-draft version. Deleting archives the form and its versions.'
                                    : 'This form only has drafts — deleting removes it permanently.'
                            }
                            onConfirm={(onSuccess) => deleteDefinition({ formDefinitionId: row.id }, { onSuccess })}
                        />
                    </Space>
                );
            },
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: listQuery.data?.rows ?? [],
        total: listQuery.data?.total ?? 0,
        loading: listQuery.isLoading,
        columns,
        rowKey: (row) => row.id.toString(),
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'form-definitions-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => trpcUtils.formDesigner.listFormDefinitions.invalidate(),
        },
    });

    return (
        <>
            <TableToolbar
                filters={
                    <FormDefinitionFilters
                        onChange={(where) => {
                            setWhereConditions(where);
                            setQueryParams((prev) => ({ ...prev, offset: 0 }));
                        }}
                    />
                }
                variant="enhanced"
                actions={
                    <Button
                        type="primary"
                        icon={<Plus />}
                        onClick={() =>
                            navigate({
                                to: '/console/$orgId/configuration/api-integrations/form-designer/$formDefinitionId',
                                params: { orgId, formDefinitionId: 'new' },
                            })
                        }
                    >
                        Create form
                    </Button>
                }
            />
            {/* A failed list query must read as a failure — an empty table
                silently claims there are no forms (or worse, hides a 403). */}
            {listQuery.error && (
                <InlineQueryError
                    compact
                    error={listQuery.error}
                    onRetry={() => void listQuery.refetch()}
                    title="Could not load forms"
                    className="mb-3"
                />
            )}
            <PrismaTable
                {...tableProps}
                onRow={(record) => ({
                    className: 'cursor-pointer',
                    onClick: () =>
                        navigate({
                            to: '/console/$orgId/configuration/api-integrations/form-designer/$formDefinitionId',
                            params: { orgId, formDefinitionId: record.id.toString() },
                        }),
                })}
            />
        </>
    );
}
